import { tx as translateInterfaceText } from '@/utils/i18n';
import { tx, getAvailableLanguages, setLanguage, type Language } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { LanguageFlag } from './LanguageFlag';
import React from "react";
import { User, CreditCard, Info, LogOut, Handshake, UsersRound, TableOfContents, Receipt, BarChart3, MousePointerClick, Phone, ClipboardList, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { useState, useMemo, useEffect, useRef } from "react";
import { useCustomAuth } from "@/contexts/CustomAuthContext";
import { useSupportTokenModal } from "@/contexts/SupportTokenContext";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { SUPPORT_WHATSAPP_URL } from "@/lib/utils/whatsapp";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";

interface UserMenuProps {
  variant?: 'header' | 'sidebar' | 'compact';
}

type MenuIconType = React.ComponentType<{ className?: string }>;

// Item do menu no padrão novo: o fundo do item fica SEMPRE transparente
// (inclusive no data-[highlighted] do Radix) e o feedback de hover vive
// no chip circular do ícone. Os overrides com "!" neutralizam o estilo
// padrão do DropdownMenuItem (fundo azul + ícone branco).
const MENU_ITEM_BASE =
  "gap-2.5 h-[40px] rounded-lg cursor-pointer group/item bg-transparent hover:bg-transparent focus:bg-transparent data-[highlighted]:bg-transparent";
const MENU_ITEM_NORMAL = "data-[highlighted]:!text-inherit [&[data-highlighted]_svg]:!text-primary";
const MENU_ITEM_DANGER = "text-destructive data-[highlighted]:!text-destructive [&[data-highlighted]_svg]:!text-destructive";

function MenuItemContent({ icon: Icon, label, danger }: { icon: MenuIconType; label: string; danger?: boolean }) {
  useLanguage();
  return (
    <>
      <div
        className={cn(
          "w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0",
          "transition-all duration-[120ms] ease-out",
          danger ? "group-hover/item:bg-destructive/10" : "group-hover/item:bg-primary/10"
        )}
      >
        <Icon
          className={cn(
            "w-4 h-4 transition-colors duration-[120ms]",
            danger
              ? "text-muted-foreground group-hover/item:text-destructive"
              : "text-muted-foreground group-hover/item:text-primary"
          )}
        />
      </div>
      <span
        className={cn(
          "text-[13px] font-medium transition-colors duration-[120ms] flex-1",
          danger
            ? "text-foreground/75 group-hover/item:text-destructive"
            : "text-foreground/75 group-hover/item:text-primary"
        )}
      >
        {label}
      </span>
    </>
  );
}

interface MenuItemProps {
  icon: MenuIconType;
  label: string;
  to?: string;
  href?: string;
  onClick?: () => void;
  danger?: boolean;
}

function MenuItem({ icon, label, to, href, onClick, danger }: MenuItemProps) {
  useLanguage();
  const className = cn(MENU_ITEM_BASE, danger ? MENU_ITEM_DANGER : MENU_ITEM_NORMAL);

  if (to) {
    return (
      <DropdownMenuItem asChild className={className}>
        <Link to={to}>
          <MenuItemContent icon={icon} label={label} danger={danger} />
        </Link>
      </DropdownMenuItem>
    );
  }

  if (href) {
    return (
      <DropdownMenuItem asChild className={className}>
        <a href={href} target="_blank" rel="noopener noreferrer">
          <MenuItemContent icon={icon} label={label} danger={danger} />
        </a>
      </DropdownMenuItem>
    );
  }

  return (
    <DropdownMenuItem onClick={onClick} className={className}>
      <MenuItemContent icon={icon} label={label} danger={danger} />
    </DropdownMenuItem>
  );
}

export function UserMenu({ variant = 'header' }: UserMenuProps) {
  const language = useLanguage();
  const { logout, user, hasPermission, isMember } = useCustomAuth();
  const { openModal } = useSupportTokenModal();

  const [open, setOpen] = useState(false);
  const [languageOptionsOpen, setLanguageOptionsOpen] = useState(false);
  const languageCloseTimer = useRef<ReturnType<typeof setTimeout>>();
  const languagePointerInside = useRef(false);
  const keepLanguageOptionsOpen = () => {
    clearTimeout(languageCloseTimer.current);
    languagePointerInside.current = true;
    setLanguageOptionsOpen(true);
  };
  const scheduleLanguageOptionsClose = () => {
    languagePointerInside.current = false;
    clearTimeout(languageCloseTimer.current);
    languageCloseTimer.current = setTimeout(() => setLanguageOptionsOpen(false), 300);
  };
  useEffect(() => () => clearTimeout(languageCloseTimer.current), []);
  const [role, SetRole] = useState("usuário")

  const canSee = (key: string) => !isMember || hasPermission(key);

  useEffect(() => {
    switch (user?.nivel) {
      case "admin":
        SetRole("Administrador");
        break;
      case "manager":
        SetRole("Afiliado Gerente");
        break;
      case "seller":
        SetRole("Afiliado vendedor");
        break;
      case "platform":
        SetRole("Sócio gerente");
        break;
      case "support":
        SetRole("Suporte e Atendimento");
        break;
      default:
        SetRole("Usuário")
    }
  }, [user?.nivel])

  const avatarUrl = useMemo(() => {
    if (!user?.avatar) return null;
    return `${user.avatar}?v=${Date.now()}`;
  }, [user?.avatar]);

  const handleLogout = async () => {
    try {
      await logout();
      window.location.replace('/');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
      toast.error(translateInterfaceText("Erro ao fazer logout"));
    }
  };

  const avatar = (
    <div className="h-8 w-8 rounded-full overflow-hidden flex-shrink-0">
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={user?.nome || tx('Usuário')}
          className="w-full h-full object-cover"
          key={avatarUrl}
        />
      ) : (
        <div className="bg-muted w-full h-full flex items-center justify-center text-xs font-medium">
          {user?.nome?.charAt(0)?.toUpperCase() || 'U'}
        </div>
      )}
    </div>
  );

  const isSellerOrManager = user?.nivel === 'seller' || user?.nivel === 'manager';
  const isNotCustomer = user?.nivel !== 'customer';

  return (
    <DropdownMenu open={open} onOpenChange={value => {
      setOpen(value);
      if (!value) {
        clearTimeout(languageCloseTimer.current);
        languagePointerInside.current = false;
        setLanguageOptionsOpen(false);
      }
    }}>
      <DropdownMenuTrigger asChild>
        {variant === 'sidebar' ? (
          <button className="w-full flex items-center gap-2.5 py-2 px-2 rounded-[8px] hover:bg-[rgba(0,0,0,0.05)] transition-colors duration-[120ms] focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(0,102,255,0.25)]">
            {avatar}
            <div className="flex flex-col items-start overflow-hidden flex-1 min-w-0">
              <span className="text-[13px] font-semibold text-[rgba(0,0,0,0.85)] truncate w-full leading-[1.3] text-left">
                {user?.nome || tx('Usuário')}
              </span>
              <span className="text-[11px] text-[rgba(0,0,0,0.45)] truncate w-full leading-[1.3] text-left">
                {user?.email || ''}
              </span>
              <span className="text-[11px] text-[rgba(0,0,0,0.45)] truncate w-full leading-[1.3] text-left">
                {tx(role)}
              </span>
            </div>
          </button>
        ) : variant === 'compact' ? (
          <button
            className={cn(
              "flex items-center justify-center rounded-full transition-all duration-150 ease-out cursor-pointer",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4F6EF7]/25",
              "h-[42px] w-[42px] p-0",
              open ? "bg-[#F1F5FF]" : "bg-transparent hover:bg-[#F1F5FF]"
            )}
            aria-label={tx("Abrir menu de conta")}
          >
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt=""
                className="w-[42px] h-[42px] rounded-full object-cover flex-shrink-0"
                key={avatarUrl}
              />
            ) : (
              <div
                className="w-[42px] h-[42px] rounded-full flex items-center justify-center text-white text-[14px] font-semibold flex-shrink-0"
                style={{ background: '#C7D6F8' }}
              >
                {user?.nome?.charAt(0)?.toUpperCase() || 'U'}
              </div>
            )}
          </button>
        ) : (
          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full overflow-hidden p-0 hover:bg-transparent hover:opacity-80 transition-opacity">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={user?.nome + " | " + tx(role)}
                className="w-full h-full object-cover"
                key={avatarUrl}
              />
            ) : (
              <div className="bg-muted w-full h-full flex items-center justify-center text-xs font-medium">
                {user?.nome?.charAt(0)?.toUpperCase() || 'U'}
              </div>
            )}
          </Button>
        )}
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align={variant === 'sidebar' ? 'center' : 'end'}
        sideOffset={6}
        collisionPadding={12}
        // O DropdownMenuContent do shadcn é `overflow-hidden` e sem altura máxima:
        // menu que não cabe na tela é cortado em silêncio, escondendo itens (o de
        // admin tem 12 itens de 40px). A var do Radix dá a altura disponível real.
        className={cn(
          "w-[218px] rounded-[14px] shadow-lg border border-border/50 p-1.5",
          "max-h-[var(--radix-dropdown-menu-content-available-height)]",
          "overflow-x-hidden overflow-y-auto sidebar-scrollbar"
        )}
        style={variant === 'sidebar' ? { zIndex: 99999 } : undefined}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        {user?.nivel !== 'member' && (<>
          <DropdownMenuGroup>
            <MenuItem icon={User} label={tx('Perfil')} to="/profile" />
            <MenuItem icon={CreditCard} label={tx('Financeiro')} to="/finance" />
            <MenuItem icon={Users} label={tx('Membros')} to="/members" />
            {user?.nivel == 'admin' && (
              <MenuItem icon={ClipboardList} label={tx('Auditoria de Membros')} to="/admin/audit-logs" />
            )}
            {(user?.nivel == 'customer') && (
              <MenuItem icon={Handshake} label={tx('Torne-se um Afiliado')} to="/afiliate" />
            )}

            <MenuItem icon={Info} label={tx('Token de Suporte')} onClick={openModal} />
          </DropdownMenuGroup>

          {(isSellerOrManager || (isNotCustomer && user?.nivel !== 'customer')) && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                {isSellerOrManager && (<>
                  <MenuItem icon={User} label={tx('Contas de Recebimento')} to="/receiveaccount" />
                  <MenuItem icon={User} label={tx('Extratos e Saques')} to="/payouts" />
                </>
                )}

                {isNotCustomer && (
                  <MenuItem icon={TableOfContents} label={tx('Minha Rede')} to="/networkList" />
                )}
              </DropdownMenuGroup>
            </>
          )}

          {/* O separador acompanha o bloco: solto, ele encostava no separador de
              baixo e virava linha dupla para quem não é admin. */}
          {user?.nivel == 'admin' && (<>
            <DropdownMenuSeparator />
            <MenuItem icon={Handshake} label={tx('Parcerias')} to="/AdminSummary" />
            <MenuItem icon={UsersRound} label={tx('Usuários')} to="/AdminUserManager" />
            <MenuItem icon={Receipt} label={tx('Custos')} to="/costs" />
            <MenuItem icon={BarChart3} label={tx('Custos por Usuário')} to="/user-cost-analysis" />
            <MenuItem icon={MousePointerClick} label={tx('Increment Clicks')} to="/IncrementClicks" />
          </>)}
          <DropdownMenuSeparator />

          <MenuItem
            icon={Phone}
            label={tx('Falar com especialista')}
            href={SUPPORT_WHATSAPP_URL}
          />

          <DropdownMenuSeparator />
        </>)}

        {isMember && canSee('perfil') && (
          <DropdownMenuGroup>
            <MenuItem icon={User} label={tx('Perfil')} to="/profile" />
            {canSee('membros') && (
              <MenuItem icon={Users} label={tx('Membros')} to="/members" />
            )}
            <MenuItem icon={Info} label={tx('Token de Suporte')} onClick={openModal} />
          </DropdownMenuGroup>
        )}

        {isMember && (
          <>
            {canSee('perfil') && <DropdownMenuSeparator />}
            <MenuItem
              icon={Phone}
              label={tx('Falar com especialista')}
              href={SUPPORT_WHATSAPP_URL}
            />
            <DropdownMenuSeparator />
          </>
        )}

        <DropdownMenuSub open={languageOptionsOpen} onOpenChange={value => {
          if (value) {
            clearTimeout(languageCloseTimer.current);
            setLanguageOptionsOpen(true);
          } else if (!languagePointerInside.current) {
            scheduleLanguageOptionsClose();
          }
        }}>
          <DropdownMenuSubTrigger
            className="rounded-lg gap-2 min-h-10 cursor-pointer"
            onPointerEnter={keepLanguageOptionsOpen}
            onPointerLeave={scheduleLanguageOptionsClose}
          >
            <LanguageFlag language={language} />
            {tx('Idioma')}
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent
              collisionPadding={12}
              sideOffset={2}
              className="min-w-40 !animate-none"
              style={{ zIndex: 100000 }}
              onPointerEnter={keepLanguageOptionsOpen}
              onPointerLeave={scheduleLanguageOptionsClose}
              onFocusOutside={event => {
                if (languagePointerInside.current) event.preventDefault();
              }}
            >
              <DropdownMenuRadioGroup value={language} onValueChange={value => setLanguage(value as Language)}>
                {getAvailableLanguages().map(item => (
                  <DropdownMenuRadioItem key={item.code} value={item.code} className="gap-2 cursor-pointer">
                    <LanguageFlag language={item.code} />
                    {item.name}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <MenuItem icon={LogOut} label={tx('Sair')} onClick={handleLogout} danger />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
